'use client';

import type { ReactNode } from 'react';
import { X } from 'lucide-react';

/** A static trait chip on a card: campus, life stage, kids, etc. */
export function Tag({
  icon,
  children,
  dark = false,
}: {
  icon?: ReactNode;
  children: ReactNode;
  /** On a See Jesus card: translucent chip on black */
  dark?: boolean;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
        dark ? 'bg-white/10 text-[#f6efe9] [&_svg]:text-[#f6efe9]/70' : 'bg-gray-100 text-[#1c2b39]'
      }`}
    >
      {icon}
      {children}
    </span>
  );
}

/** A selectable chip inside the filter sheet. */
export function ToggleChip({
  active,
  onClick,
  children,
  accent = false,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  /** Selected wears the page's --accent / --on-accent (a serve page's colour) instead of navy */
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={
        'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all hover:scale-[1.03] active:scale-95 ' +
        (active
          ? accent
            ? 'bg-[var(--accent,#1c2b39)] text-[var(--on-accent,#fff)] shadow-sm'
            : 'bg-[#1c2b39] text-white shadow-sm'
          : 'bg-white text-[#1c2b39] ring-1 ring-gray-200 ring-inset hover:bg-gray-50')
      }
    >
      {children}
    </button>
  );
}

/** An applied filter shown in the bar above the results, with a remove button. */
export function RemovableChip({
  onRemove,
  children,
}: {
  onRemove: () => void;
  children: ReactNode;
}) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#1c2b39] py-1 pr-1 pl-3 text-xs font-semibold text-white">
      {children}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove filter"
        className="flex h-5 w-5 items-center justify-center rounded-full transition-colors hover:bg-white/20"
      >
        <X className="h-3 w-3" strokeWidth={2.5} />
      </button>
    </span>
  );
}

export function StatusBadge({ full, paused }: { full: boolean; paused: boolean }) {
  if (!full && !paused) return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      {full && (
        <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold tracking-wide text-amber-800 uppercase">
          Full
        </span>
      )}
      {paused && (
        <span className="inline-flex items-center rounded-full bg-gray-200 px-2.5 py-1 text-xs font-bold tracking-wide text-gray-700 uppercase">
          On a break
        </span>
      )}
    </span>
  );
}

/** The signed-in visitor's relationship to a group. */
/** Where the visitor's (or their household's) inquiry into this group stands. */
export function InquiryBadge({ status }: { status: 'pending' | 'placed' | 'closed' }) {
  const look =
    status === 'placed'
      ? 'bg-[#299c8f] text-white'
      : status === 'closed'
        ? 'bg-gray-200 text-gray-700'
        : 'bg-[#1c2b39] text-white';
  const label =
    status === 'placed' ? 'Placed' : status === 'closed' ? 'Inquiry closed' : 'Inquiry sent';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold tracking-wide uppercase ${look}`}
    >
      {label}
    </span>
  );
}

export function MineBadge({ role }: { role: 'leader' | 'member' }) {
  return (
    <span className="inline-flex items-center rounded-full bg-[#299c8f] px-2.5 py-1 text-xs font-bold tracking-wide text-white uppercase">
      {role === 'leader' ? 'You lead' : "You're in"}
    </span>
  );
}

export type PillTone = 'teal' | 'navy' | 'amber' | 'white' | 'gray';

const TONE: Record<PillTone, string> = {
  teal: 'bg-[#299c8f] text-white',
  navy: 'bg-[#1c2b39] text-white',
  amber: 'bg-amber-100 text-amber-800',
  white: 'bg-white/90 text-[#1c2b39] shadow-sm backdrop-blur',
  gray: 'bg-gray-200 text-gray-700',
};

/** A small status pill: Featured, Full, Registration open, Today... */
export function Pill({ tone = 'gray', children }: { tone?: PillTone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold tracking-wide uppercase ${TONE[tone]}`}
    >
      {children}
    </span>
  );
}
